/**
 * PublicPortalSignupPage — `/c/<slug>/start`
 *
 * Branded self-service signup form for new clients. Submits to
 * `POST /api/public/portal/<slug>/signup` and on success redirects to
 * `/login?next=/portal&email=…` with a friendly "check your email" toast.
 *
 * The form renders the firm's custom intake questions (from
 * `portal_intake_questions` on workspace settings) below the standard
 * Name/Email/Business/Phone/Message fields.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicPortalSignupPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [engagementLetterSent, setEngagementLetterSent] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', business_name: '', phone: '', message: '',
  });
  const [intakeAnswers, setIntakeAnswers] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/portal/${slug}`);
        if (!r.ok) throw new Error();
        const b = await r.json();
        setBranding(b);
        // If public signup is disabled, bounce them back to the landing where
        // they can use the "Contact us" path instead.
        if (!b.public_signup_enabled) {
          toast.error('Signup is not enabled for this portal.');
          navigate(`/c/${slug}`, { replace: true });
        }
      } catch {
        toast.error('Portal not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  const handleIntakeChange = (qid, val) => {
    setIntakeAnswers((prev) => ({ ...prev, [qid]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    // Validate required intake questions
    const missing = (branding?.intake_questions || []).filter(
      (q) => q.required && !intakeAnswers[q.id],
    );
    if (missing.length) {
      toast.error(`Please fill in: ${missing.map((q) => q.label).join(', ')}`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/api/public/portal/${slug}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          intake_answers: intakeAnswers,
        }),
      });
      const j = await r.json();
      if (!r.ok) {
        toast.error(j.detail || 'Signup failed. Please try again.');
        return;
      }
      setEngagementLetterSent(Boolean(j.engagement_letter_sent));
      setSubmitted(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-pulse w-32 h-8 bg-zinc-200 rounded" />
      </div>
    );
  }

  if (!branding) return null;

  const brand = branding.brand_color || '#10b981';

  // Success state — friendly confirmation w/ next steps
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-zinc-50 flex items-center justify-center px-4" data-testid="portal-signup-success">
        <div className="max-w-md w-full bg-white rounded-2xl border shadow-sm p-8 text-center">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-5" style={{ background: `${brand}22` }}>
            <CheckCircle2 className="w-9 h-9" style={{ color: brand }} />
          </div>
          <h1 className="text-2xl font-bold mb-3">Welcome to {branding.name}!</h1>
          <p className="text-zinc-600 mb-4">
            Check your email — <span className="font-semibold text-zinc-900">{form.email}</span> — for your login details.
            You'll find a temporary password and a link to open the portal.
          </p>
          {engagementLetterSent && (
            <div
              className="text-left rounded-xl p-4 mb-6 text-sm leading-relaxed"
              style={{ background: `${brand}10`, border: `1px solid ${brand}33` }}
              data-testid="portal-signup-engagement-letter-notice"
            >
              <p className="font-semibold mb-1" style={{ color: brand }}>
                One more step: sign your engagement letter
              </p>
              <p className="text-zinc-700">
                We&apos;ve also emailed you a short, plain-English engagement letter to review and sign.
                It covers the work we&apos;ll do for you and how we&apos;ll work together. Click the link in
                that email when you&apos;re ready — it takes about 30 seconds.
              </p>
            </div>
          )}
          <Button
            onClick={() => navigate(`/login?next=/portal&email=${encodeURIComponent(form.email)}`)}
            className="w-full text-white"
            style={{ background: brand }}
            data-testid="portal-signup-go-to-login"
          >
            Go to login
          </Button>
          <p className="text-xs text-zinc-500 mt-4">
            Didn't get the email? Check your spam folder or contact{' '}
            {branding.contact_email ? <a href={`mailto:${branding.contact_email}`} className="underline">{branding.contact_email}</a> : branding.name}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-zinc-50" data-testid="public-portal-signup">
      {/* Branded header */}
      <header className="border-b" style={{ borderColor: `${brand}22` }}>
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to={`/c/${slug}`} className="flex items-center gap-3 hover:opacity-80">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.name} className="w-9 h-9 rounded-lg object-cover" />
            ) : (
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold"
                style={{ background: brand }}
              >
                {(branding.name || '?').charAt(0)}
              </div>
            )}
            <div>
              <p className="font-semibold leading-none">{branding.name}</p>
              <p className="text-[11px] text-zinc-500 mt-1">Client Portal</p>
            </div>
          </Link>
          <Link to={`/c/${slug}`} className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Get started with {branding.name}</h1>
          <p className="mt-3 text-zinc-600 leading-relaxed">
            Tell us a bit about yourself. We'll set up your account in a few seconds and email you a secure login link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 bg-white border rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="portal-signup-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="signup-name">Your name <span className="text-red-500">*</span></Label>
              <Input
                id="signup-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Smith"
                data-testid="signup-input-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-email">Email <span className="text-red-500">*</span></Label>
              <Input
                id="signup-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com"
                data-testid="signup-input-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-business">Business name</Label>
              <Input
                id="signup-business"
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                placeholder="Acme Inc"
                data-testid="signup-input-business"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-phone">Phone</Label>
              <Input
                id="signup-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(555) 555-0100"
                data-testid="signup-input-phone"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="signup-message">How can we help you?</Label>
            <Textarea
              id="signup-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={3}
              placeholder="Tell us about your business or what services you're looking for…"
              data-testid="signup-input-message"
            />
          </div>

          {/* Custom intake questions */}
          {(branding.intake_questions || []).map((q) => (
            <div key={q.id} className="space-y-1.5">
              <Label htmlFor={`q-${q.id}`}>
                {q.label} {q.required && <span className="text-red-500">*</span>}
              </Label>
              {q.help_text && <p className="text-xs text-zinc-500">{q.help_text}</p>}
              {q.type === 'textarea' ? (
                <Textarea
                  id={`q-${q.id}`}
                  rows={3}
                  value={intakeAnswers[q.id] || ''}
                  onChange={(e) => handleIntakeChange(q.id, e.target.value)}
                  data-testid={`signup-intake-${q.id}`}
                />
              ) : q.type === 'select' ? (
                <Select value={intakeAnswers[q.id] || ''} onValueChange={(v) => handleIntakeChange(q.id, v)}>
                  <SelectTrigger id={`q-${q.id}`} data-testid={`signup-intake-${q.id}`}>
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(q.options || []).map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : q.type === 'checkbox' ? (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`q-${q.id}`}
                    checked={intakeAnswers[q.id] === 'yes'}
                    onCheckedChange={(c) => handleIntakeChange(q.id, c ? 'yes' : 'no')}
                    data-testid={`signup-intake-${q.id}`}
                  />
                  <label htmlFor={`q-${q.id}`} className="text-sm">{q.help_text || 'Yes'}</label>
                </div>
              ) : (
                <Input
                  id={`q-${q.id}`}
                  value={intakeAnswers[q.id] || ''}
                  onChange={(e) => handleIntakeChange(q.id, e.target.value)}
                  data-testid={`signup-intake-${q.id}`}
                />
              )}
            </div>
          ))}

          <div className="pt-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <Button
              type="submit"
              disabled={submitting}
              className="text-base h-11 px-7 text-white"
              style={{ background: brand }}
              data-testid="portal-signup-submit"
            >
              {submitting ? 'Creating your account…' : (<>Get started<Send className="w-4 h-4 ml-2" /></>)}
            </Button>
            <p className="text-xs text-zinc-500">
              By signing up you agree to share this info with {branding.name}.
            </p>
          </div>
        </form>

        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3" data-testid="portal-signup-existing-client">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-medium">Already a client?</p>
            <p className="mt-0.5 text-amber-800">
              <Link to="/login?next=/portal" className="underline font-medium">Sign in here</Link> instead of creating a new account.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
